import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import {Text, Icon, Title, SharedIcon, IconPalette} from '../../../sharedComponents/components'
import {colors} from '../../../sharedComponents/colors'

import {SmartAsset} from '../../../Components/Atoms/ImageLoader'
import {IconLink} from '../../../Assets/Icons/TinyIcons'
import {IconText} from '../../../Components/Atoms/IconText'
import {CompletedTask} from '../../../state/home/site/state'
import {AssetType, AssetData} from '../../../state/rest'
import {useAppState} from '../../../state'
import RichTextInstructionsView from '../../../Components/Views/Tasks/RichTextInstructionsView'
import {getInstructionsText, normalizeInstructions} from '../../../types/richTextInstructions'

function parseLink(url: string) {
  const reg = RegExp('^http|https')
  return reg.test(url) ? url : `https://${url}`
}

export const showTaskDetails = (data: CompletedTask) => {
  const instructions = normalizeInstructions(data.instructions)
  return !!(
    (data.assets && data.assets.length) ||
    (data.description && data.description.length) ||
    getInstructionsText(instructions).length ||
    (data.links && data.links.length)
  )
}
interface TaskInstructionProps {
  data: CompletedTask
}
const TaskInstruction = ({data}: TaskInstructionProps) => {
  const {t} = useTranslation(['tasks', 'common'])
  const {actions} = useAppState()
  const assets = ((data.assets || []) as unknown) as AssetData[]
  const documentAsset = assets.find(a => (a as any).assetType === AssetType.DOCUMENT)
  const otherAssets = assets.filter(a => (a as any).assetType !== AssetType.DOCUMENT)
  const instructions = normalizeInstructions(data.instructions)

  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [isFetchingDocumentUrl, setIsFetchingDocumentUrl] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchUrl = async () => {
      if (!documentAsset?.id) {
        setDocumentUrl(null)
        return
      }
      try {
        setIsFetchingDocumentUrl(true)
        const res = await actions.getDownloadUrl(documentAsset.id)
        if (!cancelled) setDocumentUrl(res?.downloadURL || null)
      } catch (e) {
        if (!cancelled) setDocumentUrl(null)
        // eslint-disable-next-line no-console
        console.error(e)
      } finally {
        if (!cancelled) setIsFetchingDocumentUrl(false)
      }
    }

    fetchUrl()
    return () => {
      cancelled = true
    }
  }, [actions, documentAsset?.id])

  return showTaskDetails(data) ? (
    <>
      <div>
        <Title level={6} strong style={{margin: 'auto'}}>
          {t('tasks:labels.document', {defaultValue: 'Document'})}
        </Title>
        {!!documentAsset && (
          <IconText
            icon={<SharedIcon icon={IconPalette.Document} width="15" height="15" />}
            text={
              isFetchingDocumentUrl ? (
                <span>{t('common:general.loading', 'Loading...')}</span>
              ) : (
                <a
                  style={{color: colors.brand.cobalt}}
                  href={documentUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => {
                    if (!documentUrl) e.preventDefault()
                  }}
                >
                  {documentAsset.name}
                </a>
              )
            }
          />
        )}
      </div>

      {/* Keep existing behavior for photos/videos */}
      {otherAssets ? otherAssets.map(a => <SmartAsset key={a.id} asset={a as any} />) : null}
      <>
        {instructions && getInstructionsText(instructions).length > 0 && (
          <div>
            <Title level={6} strong style={{margin: 'auto'}}>
              {t('tasks:labels.howToDo', 'How to do')}
            </Title>
            <RichTextInstructionsView instructions={instructions} data-cy="modal-task-instructions" />
          </div>
        )}
        {data.description && (
          <div>
            <Title level={6} strong style={{margin: 'auto'}}>
              {t('tasks:labels.whatYouNeed', 'What you need')}
            </Title>
            <Text data-cy="modal-task-description" size="M" color={colors.system.grey_50}>
              {data.description}
            </Text>
          </div>
        )}

        {!!data.links && data.links.length > 0 && (
          <IconText
            icon={<IconLink />}
            text={
              <a
                style={{color: colors.brand.cobalt}}
                href={parseLink(data.links[0].url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.links[0].description}
              </a>
            }
          />
        )}
      </>
    </>
  ) : (
    <NoInstructionsPlaceholder>
      <Icon type="documents" width="3rem" height="5rem" fill={colors.system.grey_1} />
      <Text size="M" strong>
        {t('tasks:instructions.unavailable.title', 'There is no instruction available for this task.')}
      </Text>
      <Text size="M">
        {t('tasks:instructions.unavailable.suggestion', 'Contact your manager for more information.')}
      </Text>
    </NoInstructionsPlaceholder>
  )
}

const NoInstructionsPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
`

export default TaskInstruction
